// /src/pages/Admin.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Plus, Edit, Trash2, Save, X, Search, Filter, Grid, List, Settings, Leaf, Trees, Database, Image, MapPin, CheckCircle, AlertCircle, Loader2, Upload, Download, Info } from 'lucide-react'
import { useAuth } from '../utils/authContext'
import usePlantStore from '../utils/plantStore'
import ImageUpload from '../components/ImageUpload'
import MapComponent from '../components/MapComponent'
import Layout from '../components/Layout'

const Admin = () => {
  const { plants, addPlant, updatePlant, deletePlant, fetchPlants, clearPlants } = usePlantStore()
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingPlant, setEditingPlant] = useState(null)
  const [activeTab, setActiveTab] = useState('add')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [viewMode, setViewMode] = useState('grid')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [autoSaveTimer, setAutoSaveTimer] = useState(null)
  const [protectedImages, setProtectedImages] = useState([])
  const [showSuccessPage, setShowSuccessPage] = useState(false)
  const [successPlantData, setSuccessPlantData] = useState(null)
  const [showBulkUpload, setShowBulkUpload] = useState(false)
  const [bulkUploadData, setBulkUploadData] = useState([])
  const [bulkUploadStatus, setBulkUploadStatus] = useState(null)
  const [isBulkUploading, setIsBulkUploading] = useState(false)
  const [isImageUploading, setIsImageUploading] = useState(false)
  const [submissionId, setSubmissionId] = useState(null)
  const coordinateUpdateTimeoutRef = useRef(null);
  const submitButtonRef = useRef(null);
  const lastSubmittedPlant = useRef(null);
  const { user, logout, isSuperAdmin, isAdmin, isViewer, canDelete, canCreateUsers } = useAuth()
  const [formSubmissionLock, setFormSubmissionLock] = useState(false)
  const [showPersistenceNotice, setShowPersistenceNotice] = useState(true)

  // REMOVED: fetchPlants on mount - plants are loaded from localStorage
  // Plants are already available from plantStore, no need to fetch on mount

  const categories = [
    { id: 'all', name: 'All Plants' },
    { id: 'trees', name: 'Trees' },
    { id: 'flowers', name: 'Flowers' },
    { id: 'succulents', name: 'Succulents' },
    { id: 'palms', name: 'Palms' },
    { id: 'ferns', name: 'Ferns' },
    { id: 'climbers', name: 'Climbers' },
  ]

  const [formData, setFormData] = useState({
    scientificName: '',
    name: '',
    category: 'tree',
    image: '',
    habitat: '',
    careLevel: 'Easy',
    description: '',
    family: '',
    genus: '',
    commonName: '',
    observationDate: '',
    latitude: '',
    longitude: '',
    placeName: '',
    lifeForm: '',
    conservationStatus: '',
    observerName: ''
  })

  // SIMPLE: Basic auto-save
  useEffect(() => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer)
    }
    
    if (showAddForm && formData.name && formData.scientificName && !isSubmitting) {
      const timer = setTimeout(() => {
        localStorage.setItem('plantFormDraft', JSON.stringify(formData));
        console.log('Auto-saved form data');
      }, 2000)
      
      setAutoSaveTimer(timer)
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer)
      }
    }
  }, [formData, showAddForm, isSubmitting])

  // SIMPLE: Basic draft loading
  useEffect(() => {
    if (showAddForm && !editingPlant) {
      const draft = localStorage.getItem('plantFormDraft')
      if (draft) {
        try {
          const parsedDraft = JSON.parse(draft)
          console.log('Loading draft data:', parsedDraft);
          setFormData(parsedDraft);
        } catch (error) {
          console.error('Error loading draft:', error)
        }
      }
    }
  }, [showAddForm, editingPlant])

  // REMOVED: Conflicting image sync effects - Image is now handled separately

  // Cleanup coordinate timeout on unmount
  useEffect(() => {
    return () => {
      if (coordinateUpdateTimeoutRef.current) {
        clearTimeout(coordinateUpdateTimeoutRef.current);
      }
    };
  }, []);

  const filteredPlants = plants.filter(plant => {
    const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         plant.scientificName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (plant.family && plant.family.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = selectedCategory === 'all' || plant.category === selectedCategory
    return matchesSearch && matchesCategory
  })

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  // Prevent form submission on Enter key in any input field
  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      console.log('🔒 Preventing form submission on Enter key in input field');
      e.preventDefault();
    }
  }

  // BULLETPROOF: Simple image handling that never fails
  const handleImageUpload = (images) => {
    console.log('🖼️ Image upload received:', images);
    console.log('🖼️ Type of images:', typeof images);
    console.log('🖼️ Is array:', Array.isArray(images));
    
    // Convert to array if single image (backward compatibility)
    const imageArray = Array.isArray(images) ? images : (images ? [images] : []);
    console.log('🖼️ Converted to array:', imageArray);
    
    // ONLY accept valid image URLs
    const validImages = imageArray.filter(img => 
      img && img.trim() !== '' && img.startsWith('http')
    );
    
    console.log('✅ Setting valid images:', validImages);
    setProtectedImages(validImages);
    
    // Set uploading state based on whether we're receiving new images
    const hasNewImages = validImages.length > 0;
    setIsImageUploading(hasNewImages);
  }

  // REMOVED: All complex emergency protections - using simple approach

  const handleCoordinatesChange = useCallback((coordinates) => {
    console.log('🗺️ Coordinates changing - protected images:', protectedImages);
    
    if (coordinateUpdateTimeoutRef.current) {
      clearTimeout(coordinateUpdateTimeoutRef.current);
    }
    
    coordinateUpdateTimeoutRef.current = setTimeout(() => {
      const currentLat = formData.latitude ? parseFloat(formData.latitude) : null;
      const currentLng = formData.longitude ? parseFloat(formData.longitude) : null;
      
      const latChanged = Math.abs((currentLat || 0) - coordinates.lat) > 0.00001;
      const lngChanged = Math.abs((currentLng || 0) - coordinates.lng) > 0.00001;
      
      if (latChanged || lngChanged) {
        console.log('🗺️ Updating coordinates. Protected images during update:', protectedImages);
        setFormData(prev => ({
          ...prev,
          latitude: coordinates.lat.toFixed(6),
          longitude: coordinates.lng.toFixed(6)
          // REMOVED: image handling - Image is now separate
        }));
      }
    }, 300);
  }, [formData.latitude, formData.longitude]); // REMOVED protectedImage dependency

  const validateForm = () => {
    const errors = []
    
    if (!formData.scientificName?.trim()) {
      errors.push('Scientific name is required')
    }
    
    if (!formData.name?.trim()) {
      errors.push('Common name is required')
    }
    
    if (!formData.category) {
      errors.push('Category is required')
    }
    
    return errors
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const errors = validateForm()
    if (errors.length > 0) {
      setSubmitStatus({ type: 'error', message: errors.join(', ') })
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)
    
    try {
      // Prepare form data with images included
      const submissionData = {
        ...formData,
        images: protectedImages // Include the protected images
      }
      
      console.log('📝 Submitting plant data:', submissionData)
      
      if (editingPlant) {
        await updatePlant(editingPlant.id, submissionData)
        setEditingPlant(null)
        setSubmitStatus({ type: 'success', message: 'Plant updated successfully!' })
      } else {
        await addPlant(submissionData)
        setSubmitStatus({ type: 'success', message: 'Plant added successfully!' })
      }
      
      // Clear form data
      localStorage.removeItem('plantFormDraft')
      setProtectedImages([]);
      
      // Reset form
      setFormData({
        scientificName: '',
        name: '',
        category: 'tree',
        habitat: '',
        careLevel: 'Easy',
        description: '',
        family: '',
        genus: '',
        commonName: '',
        observationDate: '',
        latitude: '',
        longitude: '',
        placeName: '',
        lifeForm: '',
        conservationStatus: '',
        observerName: ''
      })
      
      setTimeout(() => {
        setSubmitStatus(null)
      }, 3000)
      
    } catch (error) {
      console.error('❌ Submit error:', error)
      setSubmitStatus({ type: 'error', message: error.message || 'Failed to save plant' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEdit = (plant) => {
    setEditingPlant(plant)
    setFormData({
      name: plant.name || '',
      scientificName: plant.scientificName || '',
      category: plant.category || 'trees',
      // REMOVED: image: plant.image || '' - Image is handled separately
      habitat: plant.habitat || '',
      careLevel: plant.careLevel || 'Easy',
      description: plant.description || '',
      family: plant.family || '',
      genus: plant.genus || '',
      commonName: plant.commonName || '',
      observationDate: plant.observationDate || '',
      latitude: plant.latitude || '',
      longitude: plant.longitude || '',
      placeName: plant.placeName || '',
      lifeForm: plant.lifeForm || '',
      conservationStatus: plant.conservationStatus || '',
      observerName: plant.observerName || ''
    })
    // Set the protected images for editing
    setProtectedImages((plant.images || [plant.image || '']).filter(img => img));
    setShowAddForm(true)
    setActiveTab('add')
  }

  const handleDelete = (plantId) => {
    if (window.confirm('Are you sure you want to delete this plant?')) {
      deletePlant(plantId)
    }
  }

  const handleCancel = () => {
    setShowAddForm(false)
    setEditingPlant(null)
    setSubmitStatus(null)
    setProtectedImages([]); // Clear protected images
    setFormData({
      scientificName: '',
      name: '',
      category: 'tree',
      image: '',
      habitat: '',
      careLevel: 'Easy',
      description: '',
      family: '',
      genus: '',
      commonName: '',
      observationDate: '',
      latitude: '',
      longitude: '',
      placeName: '',
      lifeForm: '',
      conservationStatus: '',
      observerName: ''
    })
  }

  // Bulk upload functions
  const handleBulkUpload = async (file) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target.result
      const lines = text.split('\n')
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''))
      
      const plants = lines.slice(1).filter(line => line.trim()).map((line, index) => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''))
        const plant = {}
        headers.forEach((header, i) => {
          plant[header] = values[i] || ''
        })
        return { ...plant, id: `bulk-${index}` }
      })
      
      setBulkUploadData(plants)
      setBulkUploadStatus({ type: 'info', message: `Found ${plants.length} plants to import` })
    }
    reader.readAsText(file)
  }

  const handleBulkImport = async () => {
    setIsBulkUploading(true)
    setBulkUploadStatus({ type: 'info', message: 'Checking for duplicates and importing plants...' })
    
    let successCount = 0
    let errorCount = 0
    let duplicateCount = 0
    
    for (const plant of bulkUploadData) {
      try {
        // Check for duplicates by name and scientific name
        const existingPlant = plants.find(p => 
          p.name.toLowerCase() === (plant.name || plant.Name || '').toLowerCase() ||
          p.scientificName.toLowerCase() === (plant.scientificName || plant['Scientific Name'] || plant['scientific_name'] || '').toLowerCase()
        )
        
        if (existingPlant) {
          console.log('Duplicate found:', plant.name, 'already exists as:', existingPlant.name)
          duplicateCount++
          continue // Skip this plant
        }
        
        // Map CSV fields to our plant structure
        const plantData = {
          name: plant.name || plant.Name || '',
          scientificName: plant.scientificName || plant['Scientific Name'] || plant['scientific_name'] || '',
          category: plant.category || plant.Category || 'trees',
          habitat: plant.habitat || plant.Habitat || '',
          careLevel: plant.careLevel || plant['Care Level'] || plant['care_level'] || 'Easy',
          description: plant.description || plant.Description || '',
          family: plant.family || plant.Family || '',
          genus: plant.genus || plant.Genus || '',
          commonName: plant.commonName || plant['Common Name'] || plant['common_name'] || '',
          observationDate: plant.observationDate || plant['Observation Date'] || plant['observation_date'] || '',
          latitude: plant.latitude || plant.Latitude || '',
          longitude: plant.longitude || plant.Longitude || '',
          placeName: plant.placeName || plant['Place Name'] || plant['place_name'] || '',
          lifeForm: plant.lifeForm || plant['Life Form'] || plant['life_form'] || '',
          conservationStatus: plant.conservationStatus || plant['Conservation Status'] || plant['conservation_status'] || '',
          observerName: plant.observerName || plant['Observer Name'] || plant['observer_name'] || '',
          image: plant.image || plant.Image || ''
        }
        
        await addPlant(plantData)
        successCount++
      } catch (error) {
        console.error('Error importing plant:', plant.name, error)
        errorCount++
      }
    }
    
    setIsBulkUploading(false)
    
    let message = `Successfully imported ${successCount} plants`
    if (duplicateCount > 0) {
      message += `, ${duplicateCount} duplicates skipped`
    }
    if (errorCount > 0) {
      message += `, ${errorCount} failed`
    }
    
    setBulkUploadStatus({ 
      type: 'success', 
      message: message
    })
    
    // Clear bulk upload data after successful import
    setTimeout(() => {
      setBulkUploadData([])
      setBulkUploadStatus(null)
      setShowBulkUpload(false)
    }, 3000)
  }

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      try {
        clearPlants();
        alert('All data has been cleared.');
      } catch (error) {
        console.error('Error clearing data:', error);
        alert('Error clearing data. Please refresh the page.');
      }
    }
  }

  const handleRemoveDuplicates = () => {
    if (window.confirm('Remove duplicate plants from the list?')) {
      console.log('🧹 User requested duplicate removal');
      // REMOVED: removeDuplicates() - No longer needed
      alert('Duplicates removed from the plant list.');
    }
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-forest-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-forest-600 mb-4">Please log in to access the admin panel.</p>
          <button onClick={() => window.history.back()} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <Layout title="Plant Database Admin">
      <div className="p-6">
        {/* Persistence Notice */}
        {showPersistenceNotice && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <Info className="h-5 w-5 text-blue-400" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800">
                  🌱 Plant Persistence Fixed!
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Plants are now properly persisted using localStorage. Your added plants will be saved locally and won't disappear when you refresh the page or navigate away.
                  </p>
                  <p className="mt-1">
                    <strong>How it works:</strong> Plants are saved to your browser's localStorage immediately when added, with API sync happening in the background.
                  </p>
                </div>
                <div className="mt-3">
                  <button
                    onClick={() => setShowPersistenceNotice(false)}
                    className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Got it, hide this notice
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Plant Database Admin</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
            >
              Add New Plant
            </button>
            <button
              onClick={handleReset}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Clear All Data
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
            {(isAdmin() || isSuperAdmin()) && (
              <button
                onClick={() => setActiveTab('add')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'add'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-forest-600 hover:text-forest-900 hover:bg-forest-50'
                }`}
              >
                Add Plant
              </button>
            )}
            {(isAdmin() || isSuperAdmin()) && (
              <button
                onClick={() => setActiveTab('bulk')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'bulk'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-forest-600 hover:text-forest-900 hover:bg-forest-50'
                }`}
              >
                Bulk Upload
              </button>
            )}
            <button
              onClick={() => setActiveTab('gallery')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'gallery'
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'text-forest-600 hover:text-forest-900 hover:bg-forest-50'
              }`}
            >
              Plant Gallery
            </button>
            {canCreateUsers() && (
              <button
                onClick={() => setActiveTab('users')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'text-forest-600 hover:text-forest-900 hover:bg-forest-50'
                }`}
              >
                User Management
              </button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {showSuccessPage ? (
            <SuccessPage 
              plantData={successPlantData}
              onBackToAddPlant={() => {
                setShowSuccessPage(false)
                setSuccessPlantData(null)
                setActiveTab('add')
                setShowAddForm(true)
              }}
            />
          ) : activeTab === 'add' ? (
            <AddPlantSection
              showAddForm={showAddForm}
              setShowAddForm={setShowAddForm}
              editingPlant={editingPlant}
              setEditingPlant={setEditingPlant}
              formData={formData}
              setFormData={setFormData}
              handleInputChange={handleInputChange}
              handleImageUpload={handleImageUpload}
              handleCoordinatesChange={handleCoordinatesChange}
              handleSubmit={handleSubmit}
              handleCancel={handleCancel}
              categories={categories}
              isSubmitting={isSubmitting}
              submitStatus={submitStatus}
              protectedImages={protectedImages}
              handleInputKeyDown={handleInputKeyDown}
              setIsImageUploading={setIsImageUploading}
              submitButtonRef={submitButtonRef}
            />
          ) : activeTab === 'bulk' ? (
            <BulkUploadSection
              bulkUploadData={bulkUploadData}
              setBulkUploadData={setBulkUploadData}
              bulkUploadStatus={bulkUploadStatus}
              isBulkUploading={isBulkUploading}
              handleBulkUpload={handleBulkUpload}
              handleBulkImport={handleBulkImport}
              categories={categories}
            />
          ) : activeTab === 'users' ? (
            <UserManagementSection />
          ) : (
            <PlantGallerySection
              plants={plants}
              filteredPlants={filteredPlants}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              viewMode={viewMode}
              setViewMode={setViewMode}
              handleEdit={handleEdit}
              handleDelete={handleDelete}
              categories={categories}
              setShowAddForm={setShowAddForm}
              setActiveTab={setActiveTab}
            />
          )}
        </div>
      </div>
    </Layout>
  )
}

// Add Plant Section Component
const AddPlantSection = ({ 
  showAddForm, 
  setShowAddForm, 
  editingPlant, 
  setEditingPlant, 
  formData, 
  setFormData, 
  handleInputChange, 
  handleImageUpload,
  handleCoordinatesChange,
  handleSubmit, 
  handleCancel,
  categories,
  isSubmitting,
  submitStatus,
  protectedImages,
  handleInputKeyDown,
  setIsImageUploading,
  submitButtonRef
}) => {
  return (
    <div>
      {!showAddForm ? (
        <div className="text-center py-12">
          <Leaf className="w-16 h-16 text-forest-300 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-forest-900 mb-2">
            {editingPlant ? 'Edit Plant' : 'Add New Plant'}
          </h2>
          <p className="text-forest-600 mb-6">
            {editingPlant 
              ? 'Update the plant information below.'
              : 'Add a new plant to your database. Fill in the details below.'
            }
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>{editingPlant ? 'Edit Plant' : 'Add Plant'}</span>
          </button>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-forest-900">
                  {editingPlant ? 'Edit Plant' : 'Add New Plant'}
                </h2>
                <p className="text-sm text-forest-600 mt-1">
                  {editingPlant 
                    ? 'Update the plant information below.'
                    : 'Add a new plant to your database. Your progress will be auto-saved.'
                  }
                </p>
              </div>
              <button
                onClick={handleCancel}
                className="text-forest-400 hover:text-forest-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Status Messages */}
              {submitStatus && (
                <div className={`p-4 rounded-md flex items-center space-x-2 success-animation ${
                  submitStatus.type === 'success'
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitStatus.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 animate-bounce" />
                  ) : (
                    <AlertCircle className="w-5 h-5" />
                  )}
                  <span className="font-medium">{submitStatus.message}</span>
                  {submitStatus.type === 'success' && (
                    <div className="ml-auto">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Basic Information */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Leaf className="w-4 h-4 text-forest-600" />
                  <h3 className="text-lg font-medium text-forest-900">Basic Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Scientific Name *
                    </label>
                    <input
                      type="text"
                      name="scientificName"
                      value={formData.scientificName}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="e.g., Monstera deliciosa"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Plant Type *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select plant type</option>
                      <option value="aquatic">Aquatic & Hydrophyte</option>
                      <option value="bamboo">Bamboo</option>
                      <option value="climber">Climber</option>
                      <option value="creeper">Creeper</option>
                      <option value="cycad">Cycad</option>
                      <option value="epiphyte">Epiphyte</option>
                      <option value="grass">Grass or Grass-like Plant</option>
                      <option value="herbaceous">Herbaceous Plant</option>
                      <option value="lithophyte">Lithophyte</option>
                      <option value="palm">Palm</option>
                      <option value="shrub">Shrub</option>
                      <option value="succulent">Succulent Plant</option>
                      <option value="tree">Tree</option>
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Common Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="e.g., Monstera"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Care Level *
                    </label>
                    <select
                      name="careLevel"
                      value={formData.careLevel}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select care level</option>
                      <option value="Easy">Easy</option>
                      <option value="Moderate">Moderate</option>
                      <option value="Difficult">Difficult</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-forest-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-forest-900 mb-4 flex items-center">
                  <Database className="w-5 h-5 mr-2" />
                  Description
                </h3>
                <div>
                  <label className="block text-sm font-medium text-forest-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    rows={4}
                    className="input-field"
                    placeholder="Describe the plant's characteristics, care requirements, growth habits, etc."
                    required
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-forest-700 mb-2">
                    Habitat *
                  </label>
                  <input
                    type="text"
                    name="habitat"
                    value={formData.habitat}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyDown}
                    required
                    className="input-field"
                    placeholder="e.g., Tropical rainforests, Desert regions"
                  />
                </div>
              </div>

              {/* Taxonomic Information */}
              <div className="bg-forest-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-forest-900 mb-4 flex items-center">
                  <Trees className="w-5 h-5 mr-2" />
                  Taxonomic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-2">
                      Family *
                    </label>
                    <input
                      type="text"
                      name="family"
                      value={formData.family}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      required
                      className="input-field"
                      placeholder="e.g., Araceae, Asparagaceae"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-2">
                      Genus *
                    </label>
                    <input
                      type="text"
                      name="genus"
                      value={formData.genus}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      required
                      className="input-field"
                      placeholder="e.g., Monstera, Sansevieria"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-2">
                      Life Form *
                    </label>
                    <input
                      type="text"
                      name="lifeForm"
                      value={formData.lifeForm}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      required
                      className="input-field"
                      placeholder="e.g., Shrub, Tree, Herb, Epiphyte"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-forest-700 mb-2">
                      Observer Name *
                    </label>
                    <input
                      type="text"
                      name="observerName"
                      value={formData.observerName}
                      onChange={handleInputChange}
                      onKeyDown={handleInputKeyDown}
                      required
                      className="input-field"
                      placeholder="e.g., John Doe, NParks Flora & Fauna Web"
                    />
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="bg-forest-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-forest-900 mb-4 flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  Plant Image
                </h3>
                <ImageUpload
                  onImageUpload={handleImageUpload}
                  currentImages={protectedImages}
                  className=""
                  onUploadStateChange={setIsImageUploading}
                />
              </div>

              {/* Location */}
              <div className="bg-forest-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-forest-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  Location Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs text-forest-600 mb-1">Latitude</label>
                    <input
                      type="number"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          console.log('📍 Latitude Enter pressed - preventing form submission');
                          e.preventDefault();
                        }
                      }}
                      step="any"
                      className="input-field"
                      placeholder="e.g., 40.7128"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-forest-600 mb-1">Longitude</label>
                    <input
                      type="number"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          console.log('📍 Longitude Enter pressed - preventing form submission');
                          e.preventDefault();
                        }
                      }}
                      step="any"
                      className="input-field"
                      placeholder="e.g., -74.0060"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <p className="text-xs text-forest-500 mb-2">
                    💡 Tip: You can manually type coordinates or use the map below to set the location
                  </p>
                </div>
                <MapComponent
                  onCoordinatesChange={handleCoordinatesChange}
                  initialLat={formData.latitude ? parseFloat(formData.latitude) : null}
                  initialLng={formData.longitude ? parseFloat(formData.longitude) : null}
                  height="300px"
                  className="mb-4"
                />
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-6 border-t border-forest-200">
                <div className="text-sm text-forest-600">
                  * Required fields
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="btn-secondary"
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary inline-flex items-center space-x-2"
                    disabled={isSubmitting}
                    ref={submitButtonRef}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        <span>{editingPlant ? 'Update Plant' : 'Add Plant'}</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

// Bulk Upload Section Component
const BulkUploadSection = ({ 
  bulkUploadData, 
  setBulkUploadData, 
  bulkUploadStatus, 
  isBulkUploading, 
  handleBulkUpload, 
  handleBulkImport,
  categories 
}) => {
  const [dragActive, setDragActive] = useState(false)

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleBulkUpload(e.dataTransfer.files[0])
    }
  }

  const handleChange = (e) => {
    e.preventDefault()
    if (e.target.files && e.target.files[0]) {
      handleBulkUpload(e.target.files[0])
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-forest-900 mb-2">Bulk Upload Plants</h2>
          <p className="text-forest-600">
            Upload a CSV file to import multiple plants at once. Download the template below to see the required format.
          </p>
        </div>

        {/* Status Messages */}
        {bulkUploadStatus && (
          <div className={`p-4 rounded-md flex items-center space-x-2 mb-6 ${
            bulkUploadStatus.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : bulkUploadStatus.type === 'error'
              ? 'bg-red-100 text-red-800 border border-red-200'
              : 'bg-blue-100 text-blue-800 border border-blue-200'
          }`}>
          {bulkUploadStatus.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : bulkUploadStatus.type === 'error' ? (
            <AlertCircle className="w-5 h-5" />
          ) : (
            <Info className="w-5 h-5" />
          )}
          <span className="font-medium">{bulkUploadStatus.message}</span>
        </div>
        )}

        {/* Upload Area */}
        <div className="mb-6">
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-forest-300 hover:border-forest-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-forest-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-forest-900 mb-2">
              Upload CSV File
            </h3>
            <p className="text-forest-600 mb-4">
              Drag and drop your CSV file here, or click to browse
            </p>
            <input
              type="file"
              accept=".csv"
              onChange={handleChange}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="btn-primary inline-flex items-center space-x-2 cursor-pointer"
            >
              <Upload className="w-4 h-4" />
              <span>Choose File</span>
            </label>
          </div>
        </div>

        {/* Template Download */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-forest-900 mb-2">CSV Template & Guidelines</h3>
          <p className="text-forest-600 mb-4">
            Download the template below to see the required column format:
          </p>
          <button
            onClick={() => {
              const csvContent = `name,scientificName,category,habitat,careLevel,description,family,genus,commonName,observationDate,latitude,longitude,placeName,lifeForm,conservationStatus,observerName,image
Monstera Deliciosa,Monstera deliciosa,trees,Tropical rainforests,Easy,A popular houseplant known for its distinctive leaves with natural holes.,Araceae,Monstera,Swiss Cheese Plant,2024-01-15,2.022882,103.311456,Singapore Botanic Gardens,Epiphyte,Least Concern,John Doe,https://example.com/image1.jpg
Snake Plant,Sansevieria trifasciata,succulents,West Africa,Very Easy,A hardy plant that can survive with minimal care and low light conditions.,Asparagaceae,Sansevieria,Mother-in-law's Tongue,2024-01-16,1.3521,103.8198,Singapore,Herbaceous,Least Concern,Jane Smith,image2.png`
              const blob = new Blob([csvContent], { type: 'text/csv' })
              const url = window.URL.createObjectURL(blob)
              const a = document.createElement('a')
              a.href = url
              a.download = 'plant_template.csv'
              a.click()
              window.URL.revokeObjectURL(url)
            }}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download Template</span>
          </button>
          
          {/* Image Guidelines */}
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-medium text-blue-900 mb-2">📸 Image Guidelines</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>URLs:</strong> Use full URLs like <code>https://example.com/image.jpg</code></li>
              <li>• <strong>Local files:</strong> Use filenames like <code>image.png</code> or <code>plant1.jpg</code></li>
              <li>• <strong>Supported formats:</strong> PNG, JPG, WebP, GIF (up to 5MB)</li>
              <li>• <strong>Leave empty:</strong> If no image available</li>
            </ul>
          </div>
          
          {/* Duplicate Prevention */}
          <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
            <h4 className="font-medium text-green-900 mb-2">🛡️ Duplicate Prevention</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• <strong>Automatic check:</strong> System prevents duplicates by name and scientific name</li>
              <li>• <strong>Case-insensitive:</strong> "Monstera" and "monstera" are treated as duplicates</li>
              <li>• <strong>Skipped safely:</strong> Duplicates are skipped, not deleted</li>
              <li>• <strong>Report included:</strong> You'll see how many duplicates were skipped</li>
            </ul>
          </div>
        </div>

        {/* Preview Data */}
        {bulkUploadData.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-forest-900 mb-4">
              Preview ({bulkUploadData.length} plants)
            </h3>
            <div className="bg-forest-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {bulkUploadData.slice(0, 6).map((plant, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-forest-200">
                    <h4 className="font-medium text-forest-900 mb-1">
                      {plant.name || plant.Name || 'Unnamed Plant'}
                    </h4>
                    <p className="text-sm text-forest-600 italic mb-2">
                      {plant.scientificName || plant['Scientific Name'] || plant['scientific_name'] || 'No scientific name'}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {plant.category && (
                        <span className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
                          {plant.category}
                        </span>
                      )}
                      {plant.careLevel && (
                        <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                          {plant.careLevel}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {bulkUploadData.length > 6 && (
                <p className="text-sm text-forest-600 mt-4 text-center">
                  ... and {bulkUploadData.length - 6} more plants
                </p>
              )}
            </div>
          </div>
        )}

        {/* Import Button */}
        {bulkUploadData.length > 0 && (
          <div className="flex justify-center">
            <button
              onClick={handleBulkImport}
              disabled={isBulkUploading}
              className={`btn-primary inline-flex items-center space-x-2 ${
                isBulkUploading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isBulkUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>Import {bulkUploadData.length} Plants</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// Plant Gallery Section Component
const PlantGallerySection = ({
  plants,
  filteredPlants,
  searchTerm,
  setSearchTerm,
  selectedCategory,
  setSelectedCategory,
  viewMode,
  setViewMode,
  handleEdit,
  handleDelete,
  categories,
  setShowAddForm,
  setActiveTab,
}) => {
  return (
    <div>
      {/* Search and Filters */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forest-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search plants..."
              value={searchTerm}
              onChange={(e) => {
                console.log('🔍 Search input changed:', e.target.value);
                setSearchTerm(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  console.log('🔍 Search Enter pressed - preventing default');
                  e.preventDefault();
                }
              }}
              className="input-field pl-10"
            />
          </div>
          <button className="btn-secondary inline-flex items-center space-x-2">
            <Filter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 mb-6">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'bg-white text-forest-600 border border-forest-200 hover:bg-forest-50'
              }`}
            >
              <span>{category.name}</span>
              <span className="bg-forest-100 text-forest-600 px-2 py-0.5 rounded-full text-xs">
                {plants.filter(p => p.category === category.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* View Mode and Add Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-white text-forest-400 hover:bg-forest-50'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-white text-forest-400 hover:bg-forest-50'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
          {(isAdmin() || isSuperAdmin()) && (
            <button
              onClick={() => {
                setActiveTab('add')
                setShowAddForm(true)
              }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Plant</span>
            </button>
          )}
        </div>
      </div>

      {/* Loading State */}
      {/* Loading State */}
      {/* Error State */}
      {/* Error State */}

      {/* Plants Grid */}
      {filteredPlants.length > 0 && (
        <div className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
            : 'grid-cols-1'
        }`}>
          {filteredPlants.map((plant) => (
            <div key={plant.id} className="bg-white rounded-lg shadow-sm border border-forest-200 overflow-hidden">
              <div className="aspect-square overflow-hidden">
                <img
                  src={plant.images && Array.isArray(plant.images) && plant.images.length > 0 
                    ? `/api/images/${plant.images[0]}` 
                    : plant.image ? `/api/images/${plant.image}` : '/api/images/placeholder.jpg'}
                  alt={plant.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/api/images/placeholder.jpg';
                  }}
                />
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-semibold text-forest-900 line-clamp-1">
                    {plant.name}
                  </h3>
                  <div className="flex items-center space-x-1">
                    {(isAdmin() || isSuperAdmin()) && (
                      <button
                        onClick={() => handleEdit(plant)}
                        className="p-2 text-forest-400 hover:text-forest-600 hover:bg-forest-50 rounded-md transition-colors"
                        title="Edit plant"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    {canDelete() && (
                      <button
                        onClick={() => handleDelete(plant.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete plant"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-sm text-forest-600 italic mb-2 line-clamp-1">
                  {plant.scientificName}
                </p>
                <p className="text-sm text-forest-700 line-clamp-2 mb-3">
                  {plant.description}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 text-xs text-forest-500">
                    <MapPin className="w-3 h-3" />
                    <span className="line-clamp-1">{plant.habitat}</span>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    plant.careLevel === 'Easy' ? 'bg-green-100 text-green-700' :
                    plant.careLevel === 'Moderate' ? 'bg-yellow-100 text-yellow-700' :
                    plant.careLevel === 'Difficult' ? 'bg-red-100 text-red-700' :
                    'bg-forest-100 text-forest-700'
                  }`}>
                    {plant.careLevel}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {plants.length === 0 ? (
        <div className="text-center py-12">
          <Database className="w-16 h-16 text-forest-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-forest-900 mb-2">Plant Database is Empty</h3>
          <p className="text-forest-600 mb-4">No plants have been added to the database yet.</p>
          <div className="bg-forest-50 border border-forest-200 rounded-lg p-4 max-w-md mx-auto mb-6">
            <p className="text-sm text-forest-700 mb-2">
              <strong>Getting Started:</strong>
            </p>
            <ul className="text-sm text-forest-600 space-y-1 text-left">
              <li>• Use the "Add Plant" tab to create your first plant</li>
              <li>• Upload images to make your plants more engaging</li>
              <li>• Add detailed descriptions and care instructions</li>
            </ul>
          </div>
          {(isAdmin() || isSuperAdmin()) && (
            <button
              onClick={() => {
                setActiveTab('add')
                setShowAddForm(true)
              }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add First Plant</span>
            </button>
          )}
        </div>
      ) : filteredPlants.length === 0 && (
        <div className="text-center py-12">
          <Database className="w-16 h-16 text-forest-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-forest-900 mb-2">
            {searchTerm ? `No plants found for "${searchTerm}"` : 'No plants found'}
          </h3>
          <p className="text-forest-600 mb-4">
            {searchTerm 
              ? 'Try different search terms or browse by category.'
              : 'Add your first plant to get started.'
            }
          </p>
          {!searchTerm && (isAdmin() || isSuperAdmin()) && (
            <button
              onClick={() => {
                setActiveTab('add')
                setShowAddForm(true)
              }}
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add First Plant</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// Success Page Component
const SuccessPage = ({ plantData, onBackToAddPlant }) => {
  const [showCelebration, setShowCelebration] = useState(true)

  useEffect(() => {
    // Auto-redirect after 4 seconds
    const timer = setTimeout(() => {
      onBackToAddPlant()
    }, 4000)

    return () => clearTimeout(timer)
  }, [onBackToAddPlant])

  useEffect(() => {
    // Celebration effect
    const celebrationEffect = () => {
      const colors = ['#10B981', '#059669', '#047857', '#065F46', '#34D399', '#6EE7B7']
      for (let i = 0; i < 30; i++) {
        setTimeout(() => {
          const particle = document.createElement('div')
          particle.className = 'fixed pointer-events-none z-50'
          particle.style.left = Math.random() * window.innerWidth + 'px'
          particle.style.top = window.innerHeight + 'px'
          particle.style.width = '10px'
          particle.style.height = '10px'
          particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)]
          particle.style.borderRadius = '50%'
          particle.style.animation = 'fall 3s linear forwards'
          document.body.appendChild(particle)
          
          setTimeout(() => {
            if (particle.parentNode) {
              particle.parentNode.removeChild(particle)
            }
          }, 3000)
        }, i * 150)
      }
    }
    
    celebrationEffect()
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
      <div className="max-w-md mx-auto text-center p-8">
        {/* Success Icon */}
        <div className="mb-6">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-green-800 mb-4">
          🌱 Plant {plantData?.action === 'updated' ? 'Updated' : 'Published'} Successfully!
        </h1>
        
        <p className="text-green-700 mb-6 text-lg">
          Your plant "{plantData?.name}" has been {plantData?.action === 'updated' ? 'updated' : 'added'} to the database.
        </p>

        {/* Plant Preview */}
        {plantData?.image && (
          <div className="mb-6">
            <img 
              src={plantData.image} 
              alt={plantData.name}
              className="w-32 h-32 object-cover rounded-lg mx-auto shadow-lg"
            />
          </div>
        )}

        {/* Plant Details */}
        <div className="bg-white rounded-lg p-4 mb-6 shadow-sm">
          <h3 className="font-semibold text-forest-900 mb-2">{plantData?.name}</h3>
          <p className="text-forest-600 italic text-sm mb-2">{plantData?.scientificName}</p>
          {plantData?.category && (
            <span className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full">
              {plantData.category}
            </span>
          )}
        </div>

        {/* Redirect Message */}
        <div className="text-green-600 mb-6">
          <p className="text-sm">Redirecting back to add plant form in a few seconds...</p>
          <div className="mt-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
          </div>
        </div>

        {/* Manual Back Button */}
        <button
          onClick={onBackToAddPlant}
          className="btn-primary inline-flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Another Plant</span>
        </button>
      </div>
    </div>
  )
}

// User Management Section Component
const UserManagementSection = () => {
  const [users, setUsers] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'viewer'
  })
  const [status, setStatus] = useState(null)

  const { user } = useAuth()

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const token = localStorage.getItem('adminToken')
              const response = await fetch('/api/auth/users', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUsers(data.users)
      } else {
        setStatus({ type: 'error', message: 'Failed to fetch users' })
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      setStatus({ type: 'error', message: 'Network error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateUser = async (e) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const token = localStorage.getItem('adminToken')
      const formData = new FormData()
      formData.append('adminToken', token)
      formData.append('email', createFormData.email)
      formData.append('password', createFormData.password)
      formData.append('name', createFormData.name)
      formData.append('role', createFormData.role)

              const response = await fetch('/api/auth/create-user', {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setStatus({ type: 'success', message: 'User created successfully!' })
        setCreateFormData({ email: '', password: '', name: '', role: 'viewer' })
        setShowCreateForm(false)
        fetchUsers()
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to create user' })
      }
    } catch (error) {
      console.error('Error creating user:', error)
      setStatus({ type: 'error', message: 'Network error' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateUser = async (userId, isActive, role) => {
    try {
      const token = localStorage.getItem('adminToken')
      const formData = new FormData()
      formData.append('adminToken', token)
      formData.append('userId', userId)
      formData.append('isActive', isActive.toString())
      formData.append('role', role)

      const response = await fetch('/api/auth/update-user', {
        method: 'POST',
        body: formData
      })

      const data = await response.json()

      if (response.ok && data.success) {
        setStatus({ type: 'success', message: 'User updated successfully!' })
        fetchUsers()
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to update user' })
      }
    } catch (error) {
      console.error('Error updating user:', error)
      setStatus({ type: 'error', message: 'Network error' })
    }
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-forest-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-forest-900">User Management</h2>
            <p className="text-sm text-forest-600 mt-1">
              Manage user accounts and permissions
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>

        {/* Status Messages */}
        {status && (
          <div className={`p-4 rounded-md flex items-center space-x-2 mb-6 ${
            status.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {status.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span className="font-medium">{status.message}</span>
          </div>
        )}

        {/* Create User Form */}
        {showCreateForm && (
          <div className="mb-6 p-4 bg-forest-50 rounded-lg border border-forest-200">
            <h3 className="text-lg font-medium text-forest-900 mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={createFormData.name}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, password: e.target.value }))}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role *
                  </label>
                  <select
                    value={createFormData.role}
                    onChange={(e) => setCreateFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="input-field"
                    required
                  >
                    <option value="viewer">Viewer</option>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary"
                >
                  {isLoading ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-forest-900">All Users</h3>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-forest-600 mx-auto"></div>
              <p className="text-forest-600 mt-2">Loading users...</p>
            </div>
          ) : (
            <div className="space-y-3">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 bg-forest-50 rounded-lg border border-forest-200">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div>
                        <h4 className="font-medium text-forest-900">{user.name}</h4>
                        <p className="text-sm text-forest-600">{user.email}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                          'bg-green-100 text-green-700'
                        }`}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {user.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateUser(user.id, user.is_active, e.target.value)}
                      className="text-sm border border-forest-200 rounded px-2 py-1"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="admin">Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                    <button
                      onClick={() => handleUpdateUser(user.id, !user.is_active, user.role)}
                      className={`px-3 py-1 rounded text-sm font-medium ${
                        user.is_active
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {user.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Admin 